import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Check, ArrowRight } from "lucide-react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function HotelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => { api.get(`/hotels/${id}`).then(({ data }) => setHotel(data)).catch(() => {}); }, [id]);

  if (!hotel) return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-4"><Skeleton className="h-96 rounded-3xl" /><Skeleton className="h-40 rounded-2xl" /></div>;

  const book = (room) => navigate("/checkout", { state: { item: {
    type: "hotel", title: `${hotel.name} — ${room.name}`, amount: room.price, currency: "USD",
    details: { hotel: hotel.name, city: hotel.city, room: room.name, beds: room.beds, star: hotel.star_rating, address: hotel.address, image: hotel.image, refundable: room.refundable },
  } } });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-3 rounded-3xl overflow-hidden">
        <img src={hotel.images?.[active] || hotel.image} alt={hotel.name} className="w-full h-[420px] object-cover" />
        <div className="grid grid-cols-2 gap-3">
          {(hotel.images || []).slice(0, 4).map((img, i) => (
            <img key={i} src={img} alt="" onClick={() => setActive(i)}
              className={`w-full h-[204px] object-cover cursor-pointer rounded-lg transition-opacity ${active === i ? "ring-2 ring-brand" : "opacity-90 hover:opacity-100"}`} />
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-8">
        <div>
          <div className="flex gap-0.5 mb-2">{Array.from({ length: hotel.star_rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}</div>
          <h1 className="font-heading font-black text-3xl text-slate-950">{hotel.name}</h1>
          <div className="flex items-center gap-1 text-slate-500 mt-2"><MapPin className="w-4 h-4" />{hotel.address}, {hotel.country}</div>
          <p className="text-slate-600 leading-relaxed mt-5">{hotel.description}</p>

          <h2 className="font-heading font-bold text-xl text-slate-950 mt-8 mb-4">Amenities</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {hotel.amenities.map((a) => <div key={a} className="flex items-center gap-2 text-slate-700"><div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-emerald-600" /></div>{a}</div>)}
          </div>

          <h2 className="font-heading font-bold text-xl text-slate-950 mt-8 mb-4">Choose your room</h2>
          <div className="space-y-3">
            {hotel.room_types.map((r, i) => (
              <div key={i} data-testid={`room-${i}`} className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:border-brand/40 transition-colors">
                <div>
                  <div className="font-heading font-bold text-slate-950">{r.name}</div>
                  <div className="text-sm text-slate-500">{r.beds}</div>
                  <div className={`text-xs font-bold mt-1 ${r.refundable ? "text-emerald-600" : "text-slate-400"}`}>{r.refundable ? "Free cancellation" : "Non-refundable"}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-black text-xl text-slate-950">{money(r.price)}</div>
                  <div className="text-xs text-slate-400 mb-2">per night</div>
                  <Button onClick={() => book(r)} data-testid={`book-room-${i}`} className="rounded-full bg-coral hover:bg-coral-hover gap-1">Reserve<ArrowRight className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside>
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <div className="bg-brand text-white rounded-lg px-3 py-2 font-heading font-black text-lg">{hotel.rating}</div>
              <div><div className="font-bold text-slate-950">Exceptional</div><div className="text-xs text-slate-500">{hotel.reviews_count} verified reviews</div></div>
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <span className="text-sm text-slate-400">from</span>
              <div className="font-heading font-black text-3xl text-slate-950">{money(hotel.price_per_night)}<span className="text-sm font-normal text-slate-400"> / night</span></div>
            </div>
            <Button onClick={() => book(hotel.room_types[0])} className="w-full mt-5 rounded-full bg-brand hover:bg-brand-hover h-12 font-heading font-bold" data-testid="reserve-now">Reserve now</Button>
            <p className="text-xs text-slate-400 text-center mt-3">No payment needed today · Pay at the property</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
